import React, { useState, useEffect, useRef, useContext } from 'react';
import { FileText, Eye, PencilSimple, ChartBar, CaretDown } from '@phosphor-icons/react';
import Alerts from '../../../shared/components/ui/Alerts';
import FormResponseEditModal from '../components/FormResponseEditModal';
import FormResponseFieldFilters from '../components/FormResponseFieldFilters';
import GenericFilters from '../../../shared/components/ui/GenericFilters';
import GenericTables from '../../../shared/components/ui/GenericTables';
import PageHeader from '../../../shared/components/ui/PageHeader';
import { userService } from '../../../services/users/userService.js';
import { formService } from '../../../services/formService.js';
import Tooltip from '../../../shared/components/ui/Tooltip.jsx';
import TintedButton from '../../../shared/components/ui/TintedButton.jsx';
import ActionMenu from '../../../shared/components/ui/ActionMenu.jsx';
import { AuthContext } from '../../../context/authContext.jsx';

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const formatFormFieldValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const renderHighlightedFormFieldValue = (value, fieldFilters) => {
  return formatFormFieldValue(value);
};

const FormResponseManagement = () => {
  const { user } = useContext(AuthContext);
  const [technicians, setTechnicians] = useState([]);
  const [formResponses, setFormResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [responseModalMode, setResponseModalMode] = useState('view');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    technicianName: '',
    clientName: '',
    formType: '',
    excludeFinalization: false
  });

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResponses, setTotalResponses] = useState(0);
  const [requiredFieldsConfig, setRequiredFieldsConfig] = useState(null);
  const [formFieldOptions, setFormFieldOptions] = useState([]);
  const [formFieldFilters, setFormFieldFilters] = useState([]);

  const [showAlerts, setshowAlerts] = useState(false);
  const [showAlertsContenido, setshowAlertsContenido] = useState('');

  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);

  // Tipos de formulario disponibles
  const formTypes = [
    { value: 'general', label: 'General' },
    { value: 'finalizacion', label: 'Finalizacion' },
    { value: 'cancelacion', label: 'Cancelacion' },
  ];

  const fetchUsers = async () => {
    try {
      const techs = await userService.getTechnicians();
      setTechnicians(techs);
    } catch (err) {
      console.error("Error al cargar usuarios:", err);
      setTechnicians([]);
    }
  };

  const fetchRequiredFields = async () => {
    try {
      const result = await formService.getRequiredFieldsConfig();
      setRequiredFieldsConfig(result);
    } catch (err) {
      console.error("Error fetching required fields config:", err);
    }
  };

  const fetchFormFieldOptions = async () => {
    try {
      const fields = await formService.getFormResponseFields();
      setFormFieldOptions(fields);
    } catch (err) {
      console.error('Error obteniendo opciones de campos de formulario:', err);
      setFormFieldOptions([]);
    }
  };
  // Construye los params de filtro actuales (sin paginación), reutilizado por fetch y export
  const buildFilterParams = () => {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.formName) params.append('formName', filters.formName);
    if (filters.taskName) params.append('taskName', filters.taskName);
    if (filters.technicianName) params.append('technicianEmail', filters.technicianName);
    if (filters.clientName) params.append('clientName', filters.clientName);
    if (filters.formType) params.append('formType', filters.formType);
    if (filters.excludeFinalization) params.append('excludeFinalization', 'true');
    if (formFieldFilters.length > 0) {
      params.append('formFieldFilters', JSON.stringify(formFieldFilters.map(({ field, op, value }) => ({ field, op, value }))));
      params.append('formFieldMode', 'all');
    }
    // Agregar filtros dinámicos basados en campos promovidos
    if (filters.formType && requiredFieldsConfig?.formtypes?.[filters.formType]?.fields) {
      requiredFieldsConfig.formtypes[filters.formType].fields.forEach(field => {
        if (filters[field.id] !== undefined && filters[field.id] !== '') {
          params.append(field.id, filters[field.id]);
        }
      });
    }
    return params;
  };

  // Función para obtener respuestas de formularios
  const fetchFormResponses = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = buildFilterParams();
      // Paginación: enviar página actual y tamaño de página
      params.append('pageSize', pageSize);
      params.append('pageCurrent', currentPage);

      const response = await formService.getFormResponses(
        Object.fromEntries(params.entries())
      );

      if (response) {

        const responses = response.responses || [];
        setFormResponses(responses);

        // Si el backend devuelve total de elementos o páginas, actualizar estado
        let totalItems = response.pagination?.totalItems ?? null;
        let backendTotalPages = response.pagination?.totalPages ?? null;

        if (totalItems != null) {
          const n = Number(totalItems);
          if (!Number.isNaN(n)) totalItems = n;
        }
        if (backendTotalPages != null) {
          const n = Number(backendTotalPages);
          if (!Number.isNaN(n)) backendTotalPages = n;
        }

        if (typeof backendTotalPages === 'number') {
          setTotalPages(Math.max(1, backendTotalPages));
          if (typeof totalItems === 'number') setTotalResponses(totalItems);
        } else if (typeof totalItems === 'number') {
          setTotalResponses(totalItems);
          setTotalPages(Math.max(1, Math.ceil(totalItems / pageSize)));
        } else {
          // Fallback: si la cantidad de items recibidos es menor que pageSize
          if (responses.length < pageSize) {
            setTotalResponses((currentPage - 1) * pageSize + responses.length);
            setTotalPages(currentPage);
          } else {
            // no conocemos total, asumimos al menos la página actual
            setTotalResponses((currentPage - 1) * pageSize + responses.length);
            setTotalPages(Math.max(1, currentPage));
          }
        }
      } else {
        setError('Error al obtener las respuestas de formularios');
      }
    } catch (err) {
      console.error('Error fetching form responses:', err);
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  // Función para actualizar una respuesta de formulario
  const updateFormResponse = async (editedForm) => {
    try {

      const response = await formService.updateFormResponse(editedForm.id, editedForm);

      if (response && response.success) {
        setShowEditModal(false);
        fetchFormResponses(); // Recargar datos


        setshowAlerts(true);
        setshowAlertsContenido('Respuesta actualizada correctamente');
        //alert('Respuesta actualizada correctamente');
      } else {
        setshowAlerts(true);
        setshowAlertsContenido('Error al actualizar la respuesta');
        //alert('Error al actualizar la respuesta');
      }
    } catch (err) {
      console.error('Error updating form response:', err);
      setshowAlerts(true);
      setshowAlertsContenido('Error de conexión');
      //alert('Error de conexión');
    }
  };

  // Función para eliminar una respuesta de formulario
  const deleteFormResponse = async (id) => {
    try {
      const response = await formService.deleteFormResponse(id);

      if (response && response.success) {
        setShowEditModal(false);
        fetchFormResponses(); // Recargar datos

        setshowAlerts(true);
        setshowAlertsContenido('Respuesta eliminada correctamente');
      } else {
        setshowAlerts(true);
        setshowAlertsContenido('Error al eliminar la respuesta');
      }
    } catch (err) {
      console.error('Error deleting form response:', err);
      setshowAlerts(true);
      setshowAlertsContenido('Error de conexión');
    }
  };

  // Función para manejar clic en una respuesta
  const handleResponseClick = (response, mode = 'view') => {
    setEditForm(response);
    setResponseModalMode(mode);
    setShowEditModal(true);
  };

  // Función para exportar respuestas a CSV
  // scope: 'filtered' usa los filtros aplicados actualmente en la vista, 'all' exporta todo
  const handleExportCsv = async (scope) => {
    setShowExportMenu(false);
    try {
      setExporting(true);
      const params = scope === 'filtered'
        ? Object.fromEntries(buildFilterParams().entries())
        : {};

      const blob = await formService.exportFormResponses(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `respuestas_formularios_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando respuestas de formularios:', err);
      setshowAlerts(true);
      setshowAlertsContenido('Error al intentar descargar el CSV');
    } finally {
      setExporting(false);
    }
  };
  // Los filtros se hacen en el backend, así que formResponses ya contiene los datos filtrados
  // No usar filter() aquí, el backend maneja todo

  // Función para limpiar filtros
  const clearFilters = () => {
    const initialFilters = {
      startDate: '',
      endDate: '',
      technicianName: '',
      clientName: '',
      formType: '',
      excludeFinalization: false
    };

    // Limpiar también campos dinámicos
    if (filters.formType && requiredFieldsConfig?.formtypes?.[filters.formType]?.fields) {
      requiredFieldsConfig.formtypes[filters.formType].fields.forEach(f => {
        initialFilters[f.id] = '';
      });
    }

    setFilters(initialFilters);
    setFormFieldFilters([]);
    setCurrentPage(1);
  };

  const addFormFieldFilter = (filter) => {
    const field = formFieldOptions.find((option) => option.id === filter.field);
    setFormFieldFilters((current) => [...current, {
      ...filter,
      label: field?.label || filter.field,
      source: field?.source || 'formData'
    }]);
    setCurrentPage(1);
  };

  const removeFormFieldFilter = (indexToRemove) => {
    setFormFieldFilters((current) => current.filter((_, index) => index !== indexToRemove));
    setCurrentPage(1);
  };

  // Función para detectar si un valor es una imagen
  // (Eliminada por ser innecesaria con los nuevos componentes genéricos)


  // Formatear fecha como DD/MM/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (isNaN(d)) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Los chips pueden filtrar por varios campos, pero la tabla muestra solo
  // la columna del primer campo agregado. Las columnas propias del tipo de
  // formulario (por ejemplo, Requiere reagenda y Con visita) se agregan aparte.
  const activeFormFieldColumns = formFieldFilters.length > 0
    ? [{
      id: formFieldFilters[0].field,
      label: formFieldFilters[0].label || formFieldFilters[0].field,
      source: formFieldFilters[0].source || 'formData'
    }]
    : [];

  // Cargar usuarios al montar
  useEffect(() => {
    fetchUsers();
    fetchRequiredFields();
    fetchFormFieldOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar respuestas cuando cambien filtros o paginación
  useEffect(() => {
    fetchFormResponses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, formFieldFilters, pageSize, currentPage]);

  // Cerrar el desplegable de exportación al hacer click fuera
  useEffect(() => {
    if (!showExportMenu) return undefined;

    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  return (
    <div>
      <PageHeader
        title="Respuesta Formularios"
        subtitle="Gestiona las respuestas de formularios enviadas por los técnicos"
        icon={<FileText size={28} />}
      />


      {/* Filtros Genéricos */}
      <GenericFilters
        columns={[
          { type: 'date', key: 'startDate', label: 'Fecha Inicio' },
          { type: 'date', key: 'endDate', label: 'Fecha Fin' },
          { type: 'text', key: 'formName', label: 'Nombre Formulario' },
          { 
            type: 'select', 
            key: 'formType', 
            label: 'Tipo', 
            options: [{value: '', label: 'Todos'}, ...formTypes], 
            optionValue: 'value', 
            optionLabel: 'label' 
          },
          { type: 'text', key: 'taskName', label: 'Título de Tarea' },
          { 
            type: 'select', 
            key: 'technicianName', 
            label: 'Técnico', 
            options: [{id: '', email: '', name: 'Todos'}, ...technicians], 
            optionValue: 'email', 
            optionLabel: 'name' 
          },
          { type: 'text', key: 'clientName', label: 'Cliente' },
          // Filtros dinámicos según el tipo de formulario seleccionado
          ...(filters.formType && requiredFieldsConfig?.formtypes?.[filters.formType]?.fields?.map(field => {
            if (field.type === 'checkbox') {
              return {
                type: 'select',
                key: field.id,
                label: field.label || field.id,
                options: [
                  { id: '', name: 'Todos' },
                  { id: 'true', name: 'Sí' },
                  { id: 'false', name: 'No' }
                ],
                optionValue: 'id',
                optionLabel: 'name'
              };
            }
            return {
              type: 'text',
              key: field.id,
              label: field.label || field.id
            };
          }) || [])
        ]}
        filters={filters}
        onFilterChange={(newFilters) => {
          setFilters(newFilters);
          setCurrentPage(1);
        }}
        onClearFilters={clearFilters}
        loading={loading}
      />

      <FormResponseFieldFilters
        fields={formFieldOptions}
        filters={formFieldFilters}
        onAdd={addFormFieldFilter}
        onRemove={removeFormFieldFilter}
      />


      {/* Lista de respuestas */}
      <div className="px-6 py-4 border-line">
        <h3 className="text-lg font-medium text-content flex items-center gap-2">
          Respuestas de Formularios  ({totalResponses})
        </h3>
        {formFieldFilters.length > 0 && (
          <p className="mt-1 text-xs text-content-muted">Mostrando solo respuestas que cumplen todos los campos filtrados.</p>
        )}
      </div>
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber mx-auto"></div>
          <p className="mt-2 text-content-muted">Cargando respuestas...</p>
        </div>
      ) : error ? (
        <div className="text-center py-8 text-error">
          <p>{error}</p>
          <button
            onClick={fetchFormResponses}
            className="mt-2 bg-error hover:bg-error/80 text-white px-4 py-2 rounded"
          >
            Reintentar
          </button>
        </div>
      ) : (
        <GenericTables
        columns={[
          { key: 'date', label: 'Fecha', render: (row) => formatDate(row.createdAt) },
          { key: 'form', label: 'Nombre Formulario', mobilePrimary: true,
            render: (row) =>  {
              const formName = row.form?.name.trim();
              if (!formName) return 'N/A';
              const isTruncated = formName.length > 20;
              const truncatedFormName = isTruncated
                ? `${formName.slice(0, 19)}…`
                : formName;
              return (
                <Tooltip
                  content={formName}
                  placement="right"
                  tabIndex={0}
                  disableOnMobile
                  triggerClassName="block max-w-full md:inline-flex"
                >
                  <span className="block break-words md:hidden" aria-label={formName}>{formName}</span>
                  <span className="hidden md:inline" aria-label={formName}>{truncatedFormName}</span>
                </Tooltip>
              );
            }},
          { key: 'type', label: 'Tipo', 
            render: (row) => {
              const type = row.form?.type || '';
              return type.charAt(0).toUpperCase() + type.slice(1);
            }},
          { key: 'title',
                label: 'Título de Tarea',
                align: 'center',
                render: (row) => {
                  const fullTitle = row.task?.title.trim();
          
                  if (!fullTitle) return 'N/A';
          
                  const isTruncated = fullTitle.length > 20;
                  const truncatedTitle = isTruncated
                    ? `${fullTitle.slice(0, 19)}…`
                    : fullTitle;
          
                  return (
                    <Tooltip
                      content={fullTitle}
                      placement="right"
                      tabIndex={0}
                      disableOnMobile
                    >
                      <span aria-label={fullTitle}>{truncatedTitle}</span>
                    </Tooltip>
                  );
                } },
          { key: 'technician', label: 'Técnico', render: (row) => row.technician?.name || 'N/A' },
          { key: 'client', label: 'Cliente', 
            render: (row) => {
              const clientName = row.task?.clientSnapshot?.name?.trim();
              if (!clientName) return 'N/A';
              const isTruncated = clientName.length > 20;
              const truncatedClientName = isTruncated
                ? `${clientName.slice(0, 19)}…`
                : clientName;
              return (
                <Tooltip
                  content={clientName}
                  placement="right"
                  tabIndex={0}
                  disableOnMobile
                >
                  <span aria-label={clientName}>{truncatedClientName}</span>
                </Tooltip>
              );
            } },
          // Columnas dinámicas inyectadas según el tipo de formulario filtrado
          ...(filters.formType && requiredFieldsConfig?.formtypes?.[filters.formType]?.fields?.map(field => ({
            key: field.id,
            label: field.label || field.id,
            render: (row) => {
              const val = row[field.id];
              if (field.type === 'checkbox' || typeof val === 'boolean') return val ? 'Sí' : 'No';
              return val ?? '-';
            }
          })) || []),
          ...activeFormFieldColumns.map((field) => ({
            key: `filtered-field-${field.id}`,
            label: field.label,
            render: (row) => renderHighlightedFormFieldValue(
              field.source === 'root' ? row[field.id] : row.formData?.[field.id],
              formFieldFilters.filter((filter) => filter.field === field.id)
            )
          })),
          {
            key: 'actions',
            label: 'Acciones',
            isAction: true,
            render: (row) => (
              <ActionMenu
                options={[
                  {
                    label: 'Ver',
                    icon: <Eye size={18} weight="bold" />,
                    onClick: () => handleResponseClick(row, 'view'),
                  },
                  ...(user?.role === 'admin' ? [{
                    label: 'Editar',
                    icon: <PencilSimple size={18} />,
                    onClick: () => handleResponseClick(row, 'edit'),
                  }] : []),
                ]}
              />
            )
          }
        ]}
        data={formResponses}
        showFooter={true}
        pagination={{
          currentPage,
          totalPages,
          pageSize,
          totalItems: totalResponses,
          onPageChange: (page) => setCurrentPage(page),
          onPageSizeChange: (size) => { setPageSize(size); setCurrentPage(1); }
        }}
        emptyMessage="No hay respuestas de formularios disponibles"
      />
      )}

      {/* Botones de Acción */}
      <div className="flex gap-3 mt-4">
        <div className="relative" ref={exportMenuRef}>
          <TintedButton
            onClick={() => setShowExportMenu((prev) => !prev)}
            disabled={exporting}
          >
            <ChartBar size={16} />
            {exporting ? 'Exportando…' : 'Exportar CSV'}
            <CaretDown size={12} />
          </TintedButton>

          {showExportMenu && (
            <div className="absolute left-0 bottom-full mb-2 w-64 bg-surface border border-line rounded-lg shadow-lg z-50">
              <button
                onClick={() => handleExportCsv('filtered')}
                className="w-full text-left px-4 py-2 text-sm text-content-muted hover:bg-surface-2 rounded-t-lg"
              >
                Exportar con filtros aplicados
              </button>
              <button
                onClick={() => handleExportCsv('all')}
                className="w-full text-left px-4 py-2 text-sm text-content-muted hover:bg-surface-2 rounded-b-lg"
              >
                Exportar todo
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal de edición */}
      <FormResponseEditModal
        isOpen={showEditModal}
        formResponse={editForm}
        initialMode={responseModalMode}
        onClose={() => setShowEditModal(false)}
        onSave={updateFormResponse}
        onDelete={deleteFormResponse}
      />
      {showAlerts && (
        <Alerts title={"Aviso"} onClose={() => setshowAlerts(false)} width='max-w-md'>
          <h1 className="text-lg font-bold mb-4 m-6 text-center">{showAlertsContenido}</h1>
        </Alerts>
      )}
    </div>
  );
};

export default FormResponseManagement;
